// ConfirmFoodModal — the non-negotiable "confirm or correct" step before an AI
// food parse is saved (SPEC §11). Pre-fills the editable fields with Claude's
// estimate; Save confirms (confirmed=1), Discard deletes the pending row.

import { useState } from "react";
import { api, ApiError, type FoodParse } from "../lib/api";

type Meal = "breakfast" | "lunch" | "dinner" | "snack";
const MEALS: Meal[] = ["breakfast", "lunch", "dinner", "snack"];

function numStr(v: number | undefined): string {
  return v == null || Number.isNaN(v) ? "" : String(Math.round(v));
}

export function ConfirmFoodModal({
  pendingId,
  parse,
  defaultMeal = "snack",
  onDone,
  onCancel,
}: {
  pendingId: number;
  parse: FoodParse;
  defaultMeal?: Meal;
  onDone: () => void;
  onCancel: () => void;
}) {
  const t = parse.totals ?? {};
  const [desc, setDesc] = useState(parse.description ?? "");
  const [meal, setMeal] = useState<Meal>(defaultMeal);
  const [cal, setCal] = useState(numStr(t.calories_kcal));
  const [protein, setProtein] = useState(numStr(t.protein_g));
  const [carbs, setCarbs] = useState(numStr(t.carbs_g));
  const [fat, setFat] = useState(numStr(t.fat_g));
  const [veg, setVeg] = useState(numStr(parse.veggie_cups));
  const [slot, setSlot] = useState(parse.meal_slot != null ? String(parse.meal_slot) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // RP plan context is present when the meal was logged against an active plan.
  const hasPlan = parse.meal_slot != null || parse.on_plan != null || parse.fit != null;
  const confidencePct =
    typeof parse.confidence === "number" ? Math.round(parse.confidence * 100) : null;

  async function save() {
    const calNum = Number(cal);
    if (!desc.trim() || !Number.isFinite(calNum) || calNum < 0) {
      setErr("Description and calories required");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.foodConfirm({
        id: pendingId,
        description: desc.trim(),
        meal_type: meal,
        calories_kcal: calNum,
        protein_g: protein ? Number(protein) : undefined,
        carbs_g: carbs ? Number(carbs) : undefined,
        fat_g: fat ? Number(fat) : undefined,
        veggie_cups: hasPlan && veg ? Number(veg) : undefined,
        meal_slot: hasPlan ? (slot ? Number(slot) : null) : undefined,
        on_plan: hasPlan ? parse.on_plan : undefined,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
      setBusy(false);
    }
  }

  async function discard() {
    setBusy(true);
    try {
      await api.deleteFood(pendingId);
    } catch {
      /* best-effort cleanup */
    }
    onCancel();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="card w-full max-w-lg animate-fade-in rounded-b-none rounded-t-2xl p-5 sm:rounded-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="eyebrow mb-1">Confirm or correct</div>
            <h2 className="page-title text-xl">Review estimate</h2>
          </div>
          {confidencePct != null && (
            <span className={confidencePct >= 60 ? "pill-neutral" : "pill-warn"}>
              {confidencePct}% confident
            </span>
          )}
        </div>

        {parse.warnings && parse.warnings.length > 0 && (
          <ul className="mb-4 space-y-1 rounded-md bg-amber-50 p-3 text-xs text-ink-muted">
            {parse.warnings.map((w, i) => (
              <li key={i}>⚠ {w}</li>
            ))}
          </ul>
        )}

        {hasPlan && (
          <div className="mb-4 rounded-md border border-accent/30 bg-accent/5 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="field-label">Plan fit</span>
              <span className={parse.on_plan ? "pill-positive" : "pill-warn"}>
                {parse.on_plan ? "on plan" : "off plan"}
              </span>
            </div>
            {parse.fit && <p className="mb-2 text-xs text-ink-muted">{parse.fit}</p>}
            <div className="grid grid-cols-2 gap-2">
              <Field label="Meal # (slot)">
                <input inputMode="numeric" value={slot} onChange={(e) => setSlot(e.target.value)} className="input" placeholder="—" />
              </Field>
              <Field label="Veg (cups)">
                <input inputMode="decimal" value={veg} onChange={(e) => setVeg(e.target.value)} className="input" />
              </Field>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <Field label="Description">
            <input value={desc} onChange={(e) => setDesc(e.target.value)} className="input" />
          </Field>

          <Field label="Meal">
            <div className="flex gap-2">
              {MEALS.map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => setMeal(m)}
                  className={`flex-1 rounded-md border px-2 py-2 text-sm capitalize transition duration-fast ${
                    meal === m
                      ? "border-accent bg-accent text-white"
                      : "border-ink-line bg-surface text-ink-muted hover:border-ink-subtle/60"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Calories (kcal)">
            <input inputMode="numeric" value={cal} onChange={(e) => setCal(e.target.value)} className="input" />
          </Field>

          <div className="grid grid-cols-3 gap-2">
            <Field label="Protein g">
              <input inputMode="decimal" value={protein} onChange={(e) => setProtein(e.target.value)} className="input" />
            </Field>
            <Field label="Carbs g">
              <input inputMode="decimal" value={carbs} onChange={(e) => setCarbs(e.target.value)} className="input" />
            </Field>
            <Field label="Fat g">
              <input inputMode="decimal" value={fat} onChange={(e) => setFat(e.target.value)} className="input" />
            </Field>
          </div>

          {parse.items && parse.items.length > 0 && (
            <details className="text-xs text-ink-subtle">
              <summary className="cursor-pointer">Items detected ({parse.items.length})</summary>
              <ul className="mt-2 space-y-0.5">
                {parse.items.map((it, i) => (
                  <li key={i}>
                    {it.name ?? "item"} — {Math.round(it.calories_kcal ?? 0)} kcal
                    {it.estimated_grams ? ` (${Math.round(it.estimated_grams)}g)` : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {err && <div className="text-sm text-loss">{err}</div>}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={discard} disabled={busy} className="btn-secondary flex-1 py-3">
              Discard
            </button>
            <button type="button" onClick={save} disabled={busy} className="btn-accent flex-1 py-3 text-base">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
